import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Card, Badge, Tabs, EmptyState, Button, Input } from '../components/design-system';
import { FileText, Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react';

const statusTabs = [
  { id: '', label: 'All' },
  { id: 'draft', label: 'Draft' },
  { id: 'review', label: 'In Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'published', label: 'Published' },
  { id: 'rejected', label: 'Rejected' },
];

export const ArticlesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [search, setSearch] = useState('');

  useEffect(() => { loadArticles(); }, [page, statusFilter]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '20');
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      const res = await api.getArticles(params.toString());
      setArticles(res.articles || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadArticles(); };

  const statusVariant = (s) => {
    const map = { draft: 'default', review: 'warning', approved: 'info', published: 'success', rejected: 'danger', archived: 'default' };
    return map[s] || 'default';
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Generated Articles</h2>
        <p className="text-sm text-gray-500">{total} articles</p>
      </div>

      <form onSubmit={handleSearch}>
        <Input icon={Search} placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </form>

      <Tabs tabs={statusTabs} activeTab={statusFilter} onChange={(id) => { setStatusFilter(id); setPage(1); }} />

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : articles.length === 0 ? (
        <EmptyState icon={FileText} title="No articles" description="Articles are generated from story clusters with multiple sources." />
      ) : (
        <div className="grid gap-3">
          {articles.map((article) => (
            <Card key={article.id} hover onClick={() => navigate(`/articles/${article.id}`)} className="!p-4">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={statusVariant(article.editorial_status)}>{article.editorial_status}</Badge>
                    <Badge>{article.category}</Badge>
                    {article.source_count && <span className="text-xs text-gray-400">{article.source_count} sources</span>}
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{article.headline}</h3>
                  {article.subheadline && <p className="text-xs text-gray-500 mt-0.5">{article.subheadline}</p>}
                  {article.summary && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{article.summary}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    {article.published_at && <span>Published {new Date(article.published_at).toLocaleDateString()}</span>}
                    {!article.published_at && <span>Created {new Date(article.created_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {article.api_views > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Eye className="h-3 w-3" /> {article.api_views}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};
